import { useToast } from '@chakra-ui/react';
import { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';
import { createId } from '@paralleldrive/cuid2';
import { useFilesTrpcClient } from '@duke-hq/svc-files-ui/FilesTrpcClientProvider';
import { uploadFileImperative } from '@duke-hq/svc-files-ui/uploadFileImperative';
import { useAddFileUrl } from '@duke-hq/svc-files-ui/useTiptapFileSrcResolver';

export function useDukeFileUpload({
  fileAttributes,
}: {
  fileAttributes: {
    attachedToKind: Parameters<typeof uploadFileImperative>[0]['attachedToKind'];
    attachedToId?: string | null;
  };
}) {
  const toast = useToast();
  const { courseId, resourceId } = useParams();

  const [isLoading, setIsLoading] = useState(false);
  const { trpcClient: filesTrpcClient, initialize: initialiseFilesClient } = useFilesTrpcClient();
  const addFileUrl = useAddFileUrl();

  const uploadFile = useCallback(
    async (file: File) => {
      if (!file.name) {
        throw new Error('File name is missing');
      }

      const fileSplit = file.name.split('.');
      if (fileSplit.length === 1) {
        throw new Error('File extension is missing');
      }

      const attachedToId = fileAttributes.attachedToId ?? resourceId ?? courseId;
      if (!attachedToId) {
        throw new Error('Missing attachedToId. Make sure to pass it in a prop fileAttributes');
      }
      const attachedToKind = fileAttributes.attachedToKind ?? (resourceId ? 'resource' : 'course');

      const extension = file.name.split('.').pop();
      const filename = `${createId()}.${extension}`;

      try {
        setIsLoading(true);

        // Rename to a CUID-based ASCII-safe filename. The filename ends up
        // embedded in the public file DRN (services/files/lib/src/drn.ts) and
        // the DRN is passed as an S3 x-amz-meta-drn header, which rejects
        // non-Latin-1 characters.
        const renamed = new File([file], filename, { type: file.type });
        await initialiseFilesClient();
        const result = await uploadFileImperative({
          file: renamed,
          actions: {
            createFiles: (input) => filesTrpcClient.file.createFiles.mutate(input),
            createPartUploadUrl: (input) => filesTrpcClient.file.createPartUploadUrl.mutate(input),
            completeFileUpload: (input) => filesTrpcClient.file.completeFileUpload.mutate(input),
            cancelFileUpload: (input) => filesTrpcClient.file.cancelFileUpload.mutate(input),
          },
          attachedToKind,
          attachedToId,
          visibility: 'public',
        });
        if (result.url !== null) {
          addFileUrl(result.fileDrn, result.url);
        }
        return { filename: result.filename, fileDrn: result.fileDrn };
      } catch (e) {
        toast({
          title: 'Error',
          description: e instanceof Error ? e.message : undefined,
          status: 'error',
          duration: 9000,
          isClosable: true,
        });
        throw e;
      } finally {
        setIsLoading(false);
      }
    },
    [
      addFileUrl,
      courseId,
      fileAttributes.attachedToId,
      fileAttributes.attachedToKind,
      filesTrpcClient,
      initialiseFilesClient,
      resourceId,
      toast,
    ],
  );

  return { uploadFile, isLoading };
}
